/**
 * @file Gestori di upload per la sincronizzazione (Upload Handlers)
 * @description Contiene i gestori per l'upload delle singole entità verso Supabase:
 * progetti, mapping entries, foto, planimetrie, punti planimetria e mappe standalone.
 * Ogni gestore gestisce CREATE, UPDATE e DELETE con conversione formato locale → remoto.
 */

import { db, Project, MappingEntry, StructureEntry, Photo, Sal, SyncQueueItem, TypologyPrice, generateId } from '../db/database';
import { buildPhotoMetadataFromTable } from '../db/mappings';
import { apiFetch } from '../lib/homeserver';
import { apiStorageFrom } from '../lib/storageShim';
import { checkForConflicts, resolveProjectConflict, resolveMappingEntryConflict } from './conflictResolution';

// ============================================
// SEZIONE: Dispatcher principale (Main Dispatcher)
// Smista l'item di sync al gestore corretto in base al tipo di entità.
// ============================================

export async function processSyncItem(item: SyncQueueItem): Promise<void> {
  switch (item.entityType) {
    case 'project':
      await syncProject(item);
      break;

    case 'mapping_entry':
      await syncMappingEntry(item);
      break;

    case 'photo':
      await syncPhoto(item);
      break;

    case 'floor_plan':
      await syncFloorPlan(item);
      break;

    case 'floor_plan_point':
      await syncFloorPlanPoint(item);
      break;

    case 'standalone_map':
      await syncStandaloneMap(item);
      break;

    case 'sal':
      await syncSal(item);
      break;

    case 'typology_price':
      await syncTypologyPrice(item);
      break;

    case 'structure_entry':
      await syncStructureEntry(item);
      break;

    default:
      throw new Error(`Unknown entity type: ${item.entityType}`);
  }
}

// ============================================
// SEZIONE: Upload Progetti (Project Upload)
// Gestisce CREATE, UPDATE e DELETE di progetti verso Supabase.
// Include rilevamento e risoluzione conflitti per gli UPDATE.
// ============================================

async function syncProject(item: SyncQueueItem): Promise<void> {
  let project = item.payload as Project;

  // =========================
  // CREATE
  // =========================
  if (item.operation === 'CREATE') {
    const supabaseProject = {
      id: project.id,
      title: project.title,
      client: project.client,
      address: project.address,
      notes: project.notes,
      floors: project.floors,
      plans: project.plans,
      use_room_numbering: project.useRoomNumbering,
      use_intervention_numbering: project.useInterventionNumbering,
      typologies: project.typologies,
      owner_id: project.ownerId,
      accessible_users: project.accessibleUsers,
      archived: Boolean(project.archived),
      // NOTE: syncEnabled is NOT synced - it's a per-device preference
      created_at: new Date(project.createdAt).toISOString(),
      updated_at: new Date(project.updatedAt).toISOString(),
      version: project.version || 1, // Add version for conflict detection
      last_modified: project.lastModified || project.updatedAt, // Add lastModified
      synced: 1
    };

    const res = await apiFetch('/api/projects', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supabaseProject)
    });
    if (!res.ok) throw new Error(`Sync projects failed: ${res.statusText}`);
  }

  // =========================
  // UPDATE
  // =========================
  if (item.operation === 'UPDATE') {
    // Check for conflicts before updating
    const { hasConflict, remote } = await checkForConflicts('project', project.id);

    if (hasConflict && remote) {
      console.log(`⚠️  Conflict detected for project ${project.id}`);

      // Resolve conflict using last-modified-wins strategy
      project = await resolveProjectConflict(project, remote, 'last-modified-wins');

      // Update local database with resolved version
      await db.projects.put(project);
      console.log(`✅ Conflict resolved for project ${project.id}`);
    }

    const supabaseProject = {
      title: project.title,
      client: project.client,
      address: project.address,
      notes: project.notes,
      floors: project.floors,
      plans: project.plans,
      use_room_numbering: project.useRoomNumbering,
      use_intervention_numbering: project.useInterventionNumbering,
      typologies: project.typologies,
      accessible_users: project.accessibleUsers,
      archived: Boolean(project.archived),
      // NOTE: syncEnabled is NOT synced - it's a per-device preference
      updated_at: new Date(project.updatedAt).toISOString(),
      version: project.version || 1, // Add version for conflict detection
      last_modified: project.lastModified || project.updatedAt, // Add lastModified
      synced: 1
    };

    const updateRes = await apiFetch('/api/projects', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: project.id, ...supabaseProject })
    });
    if (!updateRes.ok) throw new Error(`Sync projects failed: ${updateRes.statusText}`);
  }

  // =========================
  // DELETE
  // =========================
  if (item.operation === 'DELETE') {
    const res = await apiFetch(`/api/projects?id=eq.${project.id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(`Delete projects failed: ${res.statusText}`);
    return;
  }

  // Mark local as synced
  await db.projects.update(project.id, { synced: 1 });
}

// ============================================
// SEZIONE: Upload Mapping Entries (Mapping Entry Upload)
// Gestisce CREATE/UPDATE/DELETE di mapping entries verso Supabase.
// Dopo l'upsert di un'entry, aggiunge anche le foto non caricate alla coda di sync.
// ============================================

async function syncMappingEntry(item: SyncQueueItem): Promise<void> {
  let entry = item.payload as MappingEntry;

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    // Check for conflicts before syncing
    const { hasConflict, remote } = await checkForConflicts('mapping', entry.id);

    if (hasConflict && remote) {
      console.log(`⚠️  Conflict detected for mapping entry ${entry.id}`);

      // Resolve conflict using last-modified-wins strategy
      entry = await resolveMappingEntryConflict(entry, remote, 'last-modified-wins');

      // Update local database with resolved version
      await db.mappingEntries.put(entry);
      console.log(`✅ Conflict resolved for mapping entry ${entry.id}`);
    }

    // B10 — la tabella `photos` è l'unica fonte di verità: ricostruisci il campo
    // embedded dal DB invece di fidarti di entry.photos (che può divergere).
    const photosMetadata = await buildPhotoMetadataFromTable(entry.id);
    // I bigint possono essere stringa (roundtrip postgres.js) o, su righe legacy,
    // mancanti: coerciamo a numero con fallback per evitare "Invalid time value"
    // su new Date(...).toISOString() che bloccherebbe l'intera sync.
    const tsNum = Number(entry.timestamp);
    const ts = Number.isFinite(tsNum) ? tsNum : Date.now();
    const lmNum = Number(entry.lastModified);
    const lm = Number.isFinite(lmNum) ? lmNum : ts;
    const supabaseEntry = {
      id: entry.id,
      project_id: entry.projectId,
      floor: entry.floor,
      room: entry.room || null,
      intervention: entry.intervention || null,
      crossings: entry.crossings,
      to_complete: entry.toComplete || false,
      timestamp: ts,
      last_modified: lm,
      version: entry.version,
      created_by: entry.createdBy,
      modified_by: entry.modifiedBy,
      photos: photosMetadata,
      synced: 1,
      created_at: new Date(ts).toISOString(),
      updated_at: new Date(lm).toISOString()
    };

    const res = await apiFetch('/api/mapping_entries', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supabaseEntry)
    });
    if (!res.ok) {
      throw new Error(`Sync mapping_entries failed: ${res.statusText}`);
    }

    // Mark local entry as synced
    await db.mappingEntries.update(entry.id, { synced: 1 });

    // Sync photos associated with this mapping entry
    const photos = await db.photos
      .where('mappingEntryId')
      .equals(entry.id)
      .toArray();

    for (const photo of photos) {
      if (!photo.uploaded) {
        // Add photo to sync queue if not already uploaded
        const photoSyncItemId = `${entry.id}-photo-${photo.id}`;
        const existingPhotoSyncItem = await db.syncQueue.get(photoSyncItemId);
        if (existingPhotoSyncItem?.synced === 2 || existingPhotoSyncItem?.synced === 0) {
          continue;
        }

        const photoSyncItem: SyncQueueItem = {
          id: photoSyncItemId,
          operation: 'CREATE',
          entityType: 'photo',
          entityId: photo.id,
          payload: photo,
          timestamp: Date.now(),
          retryCount: 0,
          synced: 0
        };

        await db.syncQueue.put(photoSyncItem);
        console.log(`📸 Added photo ${photo.id} to sync queue`);
      }
    }
  } else if (item.operation === 'DELETE') {
    const res = await apiFetch(`/api/mapping_entries?id=eq.${entry.id}`, { method: 'DELETE' });
    if (!res.ok) {
      throw new Error(`Delete mapping_entries failed: ${res.statusText}`);
    }
  }
}

// ============================================
// SEZIONE: Upload Foto (Photo Upload)
// Gestisce upload/eliminazione di foto verso Supabase Storage.
// Per CREATE/UPDATE: carica il blob, ottiene l'URL pubblico e crea il record metadata.
// Per DELETE: rimuove il file dallo storage e il record dal database.
// ============================================

async function syncPhoto(item: SyncQueueItem): Promise<void> {
  const photoMeta = item.payload as Photo;

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    // Get the actual photo blob from IndexedDB
    const photo = await db.photos.get(photoMeta.id);

    if (!photo || !photo.blob) {
      throw new Error(`Photo blob not found: ${photoMeta.id}`);
    }

    // Upload verso lo storage tramite apiStorageFrom (homeserver proxy)
    const fileName = `${photoMeta.mappingEntryId}/${photoMeta.id}.jpg`;
    const { error: uploadError } = await apiStorageFrom('photos')
      .upload(fileName, photo.blob, {
        contentType: photo.blob.type
      });

    if (uploadError) {
      throw new Error(`Photo upload failed (homeserver): ${uploadError.message}`);
    }

    const { data: { publicUrl } } = apiStorageFrom('photos').getPublicUrl(fileName);

    let thumbnailStoragePath = fileName;
    let thumbnailPublicUrl = publicUrl;

    if (photo.thumbnailBlob) {
      const thumbnailFileName = `${photoMeta.mappingEntryId}/${photoMeta.id}_thumb.jpg`;
      // Upload thumbnail tramite apiStorageFrom (homeserver proxy)
      const { error: thumbnailUploadError } = await apiStorageFrom('photos')
        .upload(thumbnailFileName, photo.thumbnailBlob, {
          contentType: photo.thumbnailBlob.type || 'image/jpeg'
        });

      if (!thumbnailUploadError) {
        thumbnailStoragePath = thumbnailFileName;
        const { data: { publicUrl: computedThumbnailPublicUrl } } = apiStorageFrom('photos').getPublicUrl(thumbnailFileName);
        thumbnailPublicUrl = computedThumbnailPublicUrl;
      } else {
        console.warn(`Thumbnail upload failed for photo ${photoMeta.id}, falling back to main asset: ${thumbnailUploadError.message}`);
      }
    }

    // Create photo metadata record tramite homeserver API
    const isStructurePhoto = photoMeta.entryType === 'structure';
    const photoMetaRes = await apiFetch('/api/photos', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: photoMeta.id,
        ...(isStructurePhoto
          ? { structure_entry_id: photoMeta.mappingEntryId }
          : { mapping_entry_id: photoMeta.mappingEntryId }),
        storage_path: fileName,
        url: publicUrl,
        thumbnail_storage_path: thumbnailStoragePath,
        thumbnail_url: thumbnailPublicUrl,
        metadata: photoMeta.metadata,
        uploaded: true,
        created_at: new Date(photoMeta.metadata.captureTimestamp).toISOString(),
        updated_at: new Date().toISOString()
      })
    });

    if (!photoMetaRes.ok) {
      throw new Error(`Photo metadata upsert failed (homeserver): ${photoMetaRes.statusText}`);
    }

    // Mark local photo as uploaded
    await db.photos.update(photoMeta.id, {
      uploaded: true,
      storagePath: fileName,
      remoteUrl: publicUrl,
      thumbnailStoragePath,
      thumbnailRemoteUrl: thumbnailPublicUrl
    });
  } else if (item.operation === 'DELETE') {
    // Use explicit storage paths from the sync queue payload because
    // the photo may already be gone from IndexedDB when DELETE runs.
    const storagePaths = [
      photoMeta.storagePath,
      photoMeta.thumbnailStoragePath,
      photoMeta.mappingEntryId ? `${photoMeta.mappingEntryId}/${photoMeta.id}.jpg` : undefined,
    ].filter((path, index, array): path is string => Boolean(path) && array.indexOf(path) === index);

    // Elimina dallo storage tramite apiStorageFrom (homeserver proxy)
    if (storagePaths.length > 0) {
      const { error: storageError } = await apiStorageFrom('photos')
        .remove(storagePaths);

      if (storageError) {
        console.warn(`Failed to delete photo from storage: ${storageError.message}`);
        // Continue with metadata deletion even if storage deletion fails
      } else {
        console.log(`🗑️ Deleted photo from storage: ${storagePaths.join(', ')}`);
      }
    }

    // Delete metadata tramite homeserver API
    const deleteRes = await apiFetch(`/api/photos?id=eq.${photoMeta.id}`, { method: 'DELETE' });
    if (!deleteRes.ok) {
      throw new Error(`Delete photos failed (homeserver): ${deleteRes.statusText}`);
    }
  }
}

// ============================================
// SEZIONE: Upload Planimetrie (Floor Plan Upload)
// Gestisce upload/eliminazione di planimetrie verso Supabase.
// Per CREATE/UPDATE: carica i blob immagine nello storage e crea il record DB.
// Per DELETE: elimina record DB e file dallo storage.
// ============================================

async function syncFloorPlan(item: SyncQueueItem): Promise<void> {
  const floorPlan = item.payload as any; // FloorPlan type from database.ts

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    // Get the actual floor plan from IndexedDB (with blobs)
    const localFloorPlan = await db.floorPlans.get(floorPlan.id);

    if (!localFloorPlan) {
      throw new Error(`Floor plan not found: ${floorPlan.id}`);
    }

    // URL asset correnti sul remoto: usati per NON sovrascrivere una sostituzione
    // in-place (Gestione Dati) quando l'edit locale non ha toccato l'immagine.
    let remoteAssetUrls: { image_url?: string | null; thumbnail_url?: string | null; pdf_url?: string | null } | null = null;

    // --- Conflict detection ANTICIPATO: check PRIMA di qualsiasi upload asset ---
    if (localFloorPlan.remoteUpdatedAt != null) {
      try {
        const remoteCheckRes = await apiFetch(`/api/floor_plans?id=eq.${localFloorPlan.id}`);
        if (!remoteCheckRes.ok) {
          if (remoteCheckRes.status !== 404) {
            throw new Error(`Conflict pre-check failed (${remoteCheckRes.status}): ${remoteCheckRes.statusText}`);
          }
          // 404 = riga non esiste remoto, procedi con CREATE
        } else {
          const remoteCheckJson = await remoteCheckRes.json() as { data: any[] };
          const remoteRecord: { updated_at: string; image_url?: string | null; thumbnail_url?: string | null; pdf_url?: string | null } | null = remoteCheckJson.data?.[0] ?? null;
          remoteAssetUrls = remoteRecord;

          if (remoteRecord) {
            const remoteUpdatedAt = new Date(remoteRecord.updated_at).getTime();
            // Se il remote è stato modificato dopo il nostro ultimo sync → conflitto
            if (remoteUpdatedAt > localFloorPlan.remoteUpdatedAt + 5000) {
              console.warn(
                `⚠️  CONFLICT: Floor plan ${localFloorPlan.id} was modified remotely ` +
                `(remote: ${new Date(remoteUpdatedAt).toISOString()}, ` +
                `our base: ${new Date(localFloorPlan.remoteUpdatedAt).toISOString()}). ` +
                `Skipping upload to avoid overwriting remote changes. Sync to get latest version.`
              );
              // Log conflict to conflictHistory
              await db.conflictHistory.add({
                id: generateId(),
                timestamp: Date.now(),
                entityType: 'floor_plan',
                entityId: localFloorPlan.id,
                conflictType: 'timestamp',
                localVersion: { updatedAt: localFloorPlan.updatedAt, remoteUpdatedAt: localFloorPlan.remoteUpdatedAt },
                remoteVersion: { updatedAt: remoteUpdatedAt },
                resolvedVersion: null,
                strategy: 'skip_upload_floor_plan',
                autoResolved: true,
                userNotified: false,
              });
              // Incrementa retryCount così l'item non viene skippato silenziosamente all'infinito
              await db.syncQueue.update(item.id, { retryCount: (item.retryCount ?? 0) + 1 });
              return; // Do NOT upload assets né upsert — utente deve sincronizzare prima
            }
          }
        }
      } catch {
        // Errore di rete durante il check → procedi con upload (best-effort)
        console.warn(`⚠️  Could not check remote version for floor plan ${localFloorPlan.id}, proceeding with upload`);
      }
    }

    // Upload blobs to Supabase Storage when missing or when local assets changed.
    let imageUrl = localFloorPlan.imageUrl;
    let thumbnailUrl = localFloorPlan.thumbnailUrl;
    const previousImageUrl = localFloorPlan.imageUrl;
    const previousThumbnailUrl = localFloorPlan.thumbnailUrl;
    let pdfUrl = localFloorPlan.pdfUrl;
    const previousPdfUrl = localFloorPlan.pdfUrl;
    // True solo quando carichiamo NUOVI oggetti in storage: la pulizia dei
    // vecchi asset deve avvenire solo in quel caso, mai per un edit metadati.
    let assetReuploaded = false;

    // Se l'edit locale NON ha toccato l'immagine (assetDirty !== 1), il sync non è
    // autorità sull'URL: una sostituzione in-place (Gestione Dati) può aver già
    // aggiornato il remoto. Adottiamo gli URL remoti correnti per evitare di
    // ripristinare un image_url stale verso uno storage già cancellato (-> 404).
    if (localFloorPlan.assetDirty !== 1 && remoteAssetUrls) {
      if (remoteAssetUrls.image_url) imageUrl = remoteAssetUrls.image_url;
      if (remoteAssetUrls.thumbnail_url) thumbnailUrl = remoteAssetUrls.thumbnail_url;
      if (remoteAssetUrls.pdf_url) pdfUrl = remoteAssetUrls.pdf_url;
    }

    if ((!imageUrl || localFloorPlan.assetDirty === 1) && localFloorPlan.imageBlob) {
      const { uploadFloorPlan } = await import('../utils/floorPlanUtils');
      const urls = await uploadFloorPlan(
        localFloorPlan.projectId,
        localFloorPlan.floor,
        localFloorPlan.imageBlob,
        localFloorPlan.thumbnailBlob || localFloorPlan.imageBlob,
        localFloorPlan.createdBy
      );
      imageUrl = urls.fullResUrl;
      thumbnailUrl = urls.thumbnailUrl;
      assetReuploaded = true;
      await db.floorPlans.update(floorPlan.id, { imageUrl, thumbnailUrl, synced: 1 });
    }

    // Upload PDF originale indipendentemente dall'imageUrl (il PDF può esistere
    // anche quando le immagini sono già state caricate in un ciclo precedente)
    if ((!pdfUrl || localFloorPlan.assetDirty === 1) && localFloorPlan.pdfBlobBase64) {
      try {
        const { uploadFloorPlanPDF } = await import('../utils/floorPlanUtils');
        const binaryStr = atob(localFloorPlan.pdfBlobBase64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        pdfUrl = await uploadFloorPlanPDF(
          localFloorPlan.projectId,
          localFloorPlan.floor,
          pdfBlob,
          localFloorPlan.createdBy
        );
        await db.floorPlans.update(floorPlan.id, { pdfUrl });
        assetReuploaded = true;
        console.log(`📄 Uploaded PDF originale for floor plan ${floorPlan.id}`);
      } catch (pdfErr) {
        console.warn(`⚠️  Failed to upload PDF for floor plan ${floorPlan.id}:`, pdfErr);
      }
    }

    // Pulizia vecchi asset SOLO se abbiamo caricato nuovi oggetti in storage.
    // Senza questo guard, un edit metadati che adotta gli URL remoti tenterebbe
    // di cancellare lo storage stale (gia rimosso) o, peggio, file ancora vivi.
    if (
      assetReuploaded &&
      ((previousImageUrl && previousImageUrl !== imageUrl) ||
      (previousThumbnailUrl && previousThumbnailUrl !== thumbnailUrl) ||
      (previousPdfUrl && previousPdfUrl !== pdfUrl))
    ) {
      const { deleteFloorPlan } = await import('../utils/floorPlanUtils');
      try {
        await deleteFloorPlan(previousImageUrl, previousThumbnailUrl, previousPdfUrl);
      } catch (cleanupError) {
        console.warn(`Failed to clean previous floor plan assets for ${floorPlan.id}:`, cleanupError);
      }
    }

    // L'app legge *_storage_path (convertRemoteToLocalFloorPlan lo preferisce su
    // *_url): va mantenuto allineato all'url corrente, altrimenti dopo un
    // re-upload asset resta puntato al file vecchio cancellato -> 404. Deriviamo
    // il path canonico MinIO dall'url proxy; per url non-proxy (legacy) mandiamo
    // null cosi l'app fa fallback su *_url.
    const toStoragePath = (u: string | undefined | null) =>
      u && u.startsWith('/api/storage/proxy/') ? u.replace('/api/storage/proxy/', '') : null;

    // Create/update floor plan record tramite homeserver API
    const fpRes = await apiFetch('/api/floor_plans', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: localFloorPlan.id,
        project_id: localFloorPlan.projectId,
        floor: localFloorPlan.floor,
        image_url: imageUrl,
        thumbnail_url: thumbnailUrl,
        pdf_url: pdfUrl || null,
        image_storage_path: toStoragePath(imageUrl),
        thumbnail_storage_path: toStoragePath(thumbnailUrl),
        pdf_storage_path: toStoragePath(pdfUrl),
        original_filename: localFloorPlan.originalFilename,
        original_format: localFloorPlan.originalFormat,
        width: localFloorPlan.width,
        height: localFloorPlan.height,
        metadata: localFloorPlan.metadata || {},
        created_by: localFloorPlan.createdBy,
        created_at: new Date(localFloorPlan.createdAt).toISOString(),
        updated_at: new Date(localFloorPlan.updatedAt).toISOString()
      })
    });

    if (!fpRes.ok) {
      throw new Error(`Sync floor_plans failed: ${fpRes.statusText}`);
    }

    // After successful upload, update remoteUpdatedAt to match what we just wrote.
    // Persistiamo anche gli URL effettivamente scritti: se abbiamo adottato quelli
    // remoti (sostituzione in-place), il blob locale stale va invalidato così il
    // prossimo accesso scarica la nuova immagine invece di puntare a storage 404.
    await db.floorPlans.update(localFloorPlan.id, {
      remoteUpdatedAt: localFloorPlan.updatedAt,
      assetDirty: 0,
      synced: 1,
      imageUrl,
      thumbnailUrl,
      pdfUrl,
    });

    // Se abbiamo adottato URL remoti senza ri-upload (sostituzione in-place altrove),
    // il blob locale e ormai stale e va eliminato: Dexie.update ignora i valori
    // undefined, quindi usiamo modify per cancellare davvero le proprieta blob.
    if (!assetReuploaded && (imageUrl !== previousImageUrl || thumbnailUrl !== previousThumbnailUrl)) {
      await db.floorPlans.where('id').equals(localFloorPlan.id).modify((fp: any) => {
        if (imageUrl !== previousImageUrl) delete fp.imageBlob;
        if (thumbnailUrl !== previousThumbnailUrl) delete fp.thumbnailBlob;
      });
    }
  } else if (item.operation === 'DELETE') {
    // Delete tramite homeserver API
    const delRes = await apiFetch(`/api/floor_plans?id=eq.${floorPlan.id}`, { method: 'DELETE' });
    if (!delRes.ok) {
      throw new Error(`Delete floor_plans failed: ${delRes.statusText}`);
    }

    // Delete from Storage if URLs exist
    if (floorPlan.imageUrl || floorPlan.thumbnailUrl || floorPlan.pdfUrl) {
      const { deleteFloorPlan } = await import('../utils/floorPlanUtils');
      try {
        await deleteFloorPlan(floorPlan.imageUrl, floorPlan.thumbnailUrl, floorPlan.pdfUrl);
      } catch (err) {
        console.warn('Failed to delete floor plan from storage:', err);
      }
    }
  }
}

// ============================================
// SEZIONE: Upload Punti Planimetria (Floor Plan Point Upload)
// Gestisce CREATE/UPDATE/DELETE di punti sulla planimetria verso Supabase.
// ============================================

async function syncFloorPlanPoint(item: SyncQueueItem): Promise<void> {
  const point = item.payload as any; // FloorPlanPoint type

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    // Get latest local point data (may have remoteUpdatedAt)
    const localPoint = await db.floorPlanPoints.get(point.id);
    const effectivePoint = localPoint || point;

    // --- Conflict detection: check if remote has been modified since our last sync ---
    if (effectivePoint.remoteUpdatedAt != null) {
      try {
        const pointCheckRes = await apiFetch(`/api/floor_plan_points?id=eq.${effectivePoint.id}`);
        if (!pointCheckRes.ok) {
          if (pointCheckRes.status !== 404) {
            throw new Error(`Conflict pre-check failed (${pointCheckRes.status}): ${pointCheckRes.statusText}`);
          }
          // 404 = riga non esiste remoto, procedi con CREATE
        } else {
          const pointCheckJson = await pointCheckRes.json() as { data: any[] };
          const remoteRecord: { updated_at: string } | null = pointCheckJson.data?.[0] ?? null;

          if (remoteRecord) {
            const remoteUpdatedAt = new Date(remoteRecord.updated_at).getTime();
            if (remoteUpdatedAt > effectivePoint.remoteUpdatedAt + 5000) {
              console.warn(
                `⚠️  CONFLICT: Floor plan point ${effectivePoint.id} was modified remotely ` +
                `(remote: ${new Date(remoteUpdatedAt).toISOString()}, ` +
                `our base: ${new Date(effectivePoint.remoteUpdatedAt).toISOString()}). ` +
                `Skipping upload to avoid overwriting remote changes.`
              );
              await db.conflictHistory.add({
                id: generateId(),
                timestamp: Date.now(),
                entityType: 'floor_plan_point',
                entityId: effectivePoint.id,
                conflictType: 'timestamp',
                localVersion: { updatedAt: effectivePoint.updatedAt, remoteUpdatedAt: effectivePoint.remoteUpdatedAt },
                remoteVersion: { updatedAt: remoteUpdatedAt },
                resolvedVersion: null,
                strategy: 'skip_upload_floor_plan_point',
                autoResolved: true,
                userNotified: false,
              });
              // Incrementa retryCount così l'item non viene skippato silenziosamente all'infinito
              await db.syncQueue.update(item.id, { retryCount: (item.retryCount ?? 0) + 1 });
              return;
            }
          }
        }
      } catch {
        console.warn(`⚠️  Could not check remote version for floor plan point ${effectivePoint.id}, proceeding with upload`);
      }
    }

    // Determine correct FK column: structure entries must go to structure_entry_id,
    // not mapping_entry_id (which has an FK constraint to mapping_entries only).
    // Handle legacy data where structure entry IDs were incorrectly stored in mappingEntryId.
    let resolvedMappingEntryId: string | null = null;
    let resolvedStructureEntryId: string | null = null;

    if (effectivePoint.structureEntryId) {
      resolvedStructureEntryId = effectivePoint.structureEntryId;
      resolvedMappingEntryId = null; // structure e mapping sono esclusivi per constraint DB fpp_exactly_one_parent
    } else if (effectivePoint.mappingEntryId) {
      const isStructureEntry = !!(await db.structureEntries.get(effectivePoint.mappingEntryId));
      if (isStructureEntry) {
        resolvedStructureEntryId = effectivePoint.mappingEntryId;
        // Migrate local record so future syncs use the correct field
        await db.floorPlanPoints.update(effectivePoint.id, { structureEntryId: effectivePoint.mappingEntryId });
      } else {
        resolvedMappingEntryId = effectivePoint.mappingEntryId;
      }
    }

    const fppRes = await apiFetch('/api/floor_plan_points', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: point.id,
        floor_plan_id: point.floorPlanId,
        mapping_entry_id: resolvedMappingEntryId,
        structure_entry_id: resolvedStructureEntryId,
        point_type: point.pointType,
        point_x: point.pointX,
        point_y: point.pointY,
        label_x: point.labelX,
        label_y: point.labelY,
        perimeter_points: point.perimeterPoints,
        custom_text: point.customText,
        ei_rating: point.eiRating ?? null,
        metadata: { ...(point.metadata || {}), ...(point.eiRating != null ? { eiRating: point.eiRating } : {}) },
        created_by: point.createdBy,
        created_at: new Date(point.createdAt).toISOString(),
        updated_at: new Date(point.updatedAt).toISOString()
      })
    });

    if (!fppRes.ok) {
      throw new Error(`Sync floor_plan_points failed: ${fppRes.statusText}`);
    }

    // After successful upload, update remoteUpdatedAt
    await db.floorPlanPoints.update(point.id, {
      remoteUpdatedAt: point.updatedAt,
      synced: 1,
    });
  } else if (item.operation === 'DELETE') {
    const delRes = await apiFetch(`/api/floor_plan_points?id=eq.${point.id}`, { method: 'DELETE' });
    if (!delRes.ok) {
      throw new Error(`Delete floor_plan_points failed: ${delRes.statusText}`);
    }
  }
}

// ============================================
// SEZIONE: Upload Mappe Standalone (Standalone Map Upload)
// Gestisce CREATE/UPDATE/DELETE di mappe standalone verso Supabase.
// Per CREATE/UPDATE: carica blob immagine e crea record DB.
// Per DELETE: elimina record DB e file dallo storage.
// ============================================

async function syncStandaloneMap(item: SyncQueueItem): Promise<void> {
  const map = item.payload as any; // StandaloneMap type

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    const localMap = await db.standaloneMaps.get(map.id);

    if (!localMap) {
      throw new Error(`Standalone map not found: ${map.id}`);
    }

    // Upload blobs to Supabase Storage if not already uploaded
    let imageUrl = localMap.imageUrl;
    let thumbnailUrl = localMap.thumbnailUrl;
    let pdfUrl = localMap.pdfUrl;

    let pdfBlob: Blob | undefined;
    if (localMap.pdfBlobBase64) {
      const { base64ToBlob } = await import('../utils/floorPlanUtils');
      pdfBlob = base64ToBlob(localMap.pdfBlobBase64, 'application/pdf');
    }

    if (!imageUrl && localMap.imageBlob) {
      const { uploadStandaloneMap } = await import('../utils/floorPlanUtils');
      const urls = await uploadStandaloneMap(
        localMap.id,
        localMap.imageBlob,
        localMap.thumbnailBlob || localMap.imageBlob,
        localMap.userId,
        pdfUrl ? undefined : pdfBlob
      );
      imageUrl = urls.fullResUrl;
      thumbnailUrl = urls.thumbnailUrl;
      pdfUrl = urls.pdfUrl || pdfUrl;

      // Update local record with URLs
      await db.standaloneMaps.update(map.id, { imageUrl, thumbnailUrl, pdfUrl, synced: 1 });
    }

    if (!pdfUrl && pdfBlob) {
      try {
        const { uploadStandaloneMapPDF } = await import('../utils/floorPlanUtils');
        pdfUrl = await uploadStandaloneMapPDF(localMap.id, pdfBlob, localMap.userId);
        await db.standaloneMaps.update(map.id, { pdfUrl });
        console.log(`📄 Uploaded PDF originale for standalone map ${map.id}`);
      } catch (pdfErr) {
        console.warn(`⚠️  Failed to upload PDF for standalone map ${map.id}:`, pdfErr);
      }
    }

    // Create/update standalone map record tramite homeserver API
    const smRes = await apiFetch('/api/standalone_maps', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: localMap.id,
        user_id: localMap.userId,
        name: localMap.name,
        description: localMap.description,
        image_url: imageUrl,
        thumbnail_url: thumbnailUrl,
        pdf_url: pdfUrl || null,
        original_filename: localMap.originalFilename,
        original_format: localMap.originalFormat || null,
        width: localMap.width,
        height: localMap.height,
        points: localMap.points.map(point => ({
          id: point.id,
          pointType: point.pointType,
          pointX: point.pointX,
          pointY: point.pointY,
          labelX: point.labelX,
          labelY: point.labelY,
          perimeterPoints: point.perimeterPoints,
          customText: point.customText,
          labelText: point.labelText,
          labelBackgroundColor: point.labelBackgroundColor,
          labelTextColor: point.labelTextColor,
          eiRating: point.eiRating ?? null,
        })),
        grid_enabled: localMap.gridEnabled,
        grid_config: localMap.gridConfig,
        metadata: localMap.metadata || {},
        created_at: new Date(localMap.createdAt).toISOString(),
        updated_at: new Date(localMap.updatedAt).toISOString()
      })
    });

    if (!smRes.ok) {
      throw new Error(`Sync standalone_maps failed: ${smRes.statusText}`);
    }

    await db.standaloneMaps.update(map.id, {
      imageUrl,
      thumbnailUrl,
      pdfUrl,
      synced: 1,
    });
  } else if (item.operation === 'DELETE') {
    const delRes = await apiFetch(`/api/standalone_maps?id=eq.${map.id}`, { method: 'DELETE' });
    if (!delRes.ok) {
      throw new Error(`Delete standalone_maps failed: ${delRes.statusText}`);
    }

    // Delete from Storage if URLs exist
    if (map.imageUrl || map.thumbnailUrl) {
      const { deleteFloorPlan } = await import('../utils/floorPlanUtils');
      try {
        await deleteFloorPlan(map.imageUrl, map.thumbnailUrl);
      } catch (err) {
        console.warn('Failed to delete standalone map from storage:', err);
      }
    }
  }
}

// ============================================
// SEZIONE: Upload SAL (SAL Upload)
// Gestisce CREATE/UPDATE/DELETE di SAL verso Supabase.
// ============================================

async function syncSal(item: SyncQueueItem): Promise<void> {
  const sal = item.payload as Sal;

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    const supabaseSal = {
      id: sal.id,
      project_id: sal.projectId,
      number: sal.number,
      name: sal.name || null,
      date: sal.date,
      notes: sal.notes || null,
      created_at: new Date(sal.createdAt).toISOString(),
      synced: true,
    };

    const salRes = await apiFetch('/api/sals', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supabaseSal)
    });
    if (!salRes.ok) {
      throw new Error(`Sync sals failed: ${salRes.statusText}`);
    }

    await db.sals.update(sal.id, { synced: 1 });
  } else if (item.operation === 'DELETE') {
    const delRes = await apiFetch(`/api/sals?id=eq.${sal.id}`, { method: 'DELETE' });
    if (!delRes.ok) {
      throw new Error(`Delete sals failed: ${delRes.statusText}`);
    }
  }
}

async function syncTypologyPrice(item: SyncQueueItem): Promise<void> {
  const price = item.payload as TypologyPrice;

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    const tpRes = await apiFetch('/api/typology_prices', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: price.id,
        project_id: price.projectId,
        attraversamento: price.attraversamento,
        tipologico_id: price.tipologicoId || null,
        price_per_unit: price.pricePerUnit,
        unit: price.unit,
        created_at: price.createdAt ? new Date(price.createdAt).toISOString() : new Date().toISOString(),
        updated_at: new Date(price.updatedAt || Date.now()).toISOString(),
      })
    });
    if (!tpRes.ok) {
      throw new Error(`Sync typology_prices failed: ${tpRes.statusText}`);
    }

    await db.typologyPrices.update(price.id, {
      synced: 1,
      updatedAt: price.updatedAt || Date.now(),
    });
  } else if (item.operation === 'DELETE') {
    const delRes = await apiFetch(`/api/typology_prices?id=eq.${price.id}`, { method: 'DELETE' });
    if (!delRes.ok) {
      throw new Error(`Delete typology_prices failed: ${delRes.statusText}`);
    }
  }
}

// ============================================
// SEZIONE: Upload Structure Entries (Structure Entry Upload)
// Gestisce CREATE/UPDATE/DELETE di structure entries verso Supabase.
// Speculare a syncMappingEntry.
// ============================================

async function syncStructureEntry(item: SyncQueueItem): Promise<void> {
  const entry = item.payload as StructureEntry;

  if (item.operation === 'CREATE' || item.operation === 'UPDATE') {
    // B10 — la tabella `photos` è l'unica fonte di verità: ricostruisci il campo
    // embedded dal DB invece di fidarti di entry.photos (che può divergere).
    const photosMetadata = await buildPhotoMetadataFromTable(entry.id);
    const supabaseEntry = {
      id: entry.id,
      project_id: entry.projectId,
      floor: entry.floor,
      room: entry.room || null,
      intervention: entry.intervention || null,
      structures: entry.structures,
      to_complete: entry.toComplete || false,
      timestamp: entry.timestamp,
      last_modified: entry.lastModified,
      version: entry.version,
      created_by: entry.createdBy,
      modified_by: entry.modifiedBy,
      photos: photosMetadata,
      synced: true,
      created_at: new Date(entry.timestamp).toISOString(),
      updated_at: new Date(entry.lastModified).toISOString()
    };

    const seRes = await apiFetch('/api/structure_entries', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supabaseEntry)
    });
    if (!seRes.ok) {
      throw new Error(`Sync structure_entries failed: ${seRes.statusText}`);
    }

    await db.structureEntries.update(entry.id, { synced: 1 });

    // Sync photos associated with this structure entry
    const photos = await db.photos
      .where('mappingEntryId')
      .equals(entry.id)
      .toArray();

    for (const photo of photos) {
      if (!photo.uploaded) {
        const photoSyncItemId = `${entry.id}-photo-${photo.id}`;
        const existingPhotoSyncItem = await db.syncQueue.get(photoSyncItemId);
        if (existingPhotoSyncItem?.synced === 2 || existingPhotoSyncItem?.synced === 0) {
          continue;
        }

        const photoSyncItem: SyncQueueItem = {
          id: photoSyncItemId,
          operation: 'CREATE',
          entityType: 'photo',
          entityId: photo.id,
          payload: photo,
          timestamp: Date.now(),
          retryCount: 0,
          synced: 0
        };

        await db.syncQueue.put(photoSyncItem);
      }
    }
  } else if (item.operation === 'DELETE') {
    const delRes = await apiFetch(`/api/structure_entries?id=eq.${entry.id}`, { method: 'DELETE' });
    if (!delRes.ok) {
      throw new Error(`Delete structure_entries failed: ${delRes.statusText}`);
    }
  }
}
